import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { fractionNear, labelInk, labelPoint, labelTextWidth, pointAlong, type ConnectorGeometry, type ConnectorPath } from '../../doc/connector';
import { distance, type Point } from '../../doc/geometry';
import type { ConnectorItem, ConnectorProps } from '../../doc/schema';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { screenToWorld } from '../viewport/camera';
import { commitConnectorLabel } from './connectors';

/** Screen pixels within which a dragged label snaps to the middle of the path. */
const MIDDLE_SNAP_PX = 8;

/**
 * Dragging a connector's label along its path: it keeps the spot it was grabbed by, snaps to the
 * middle, shows live through the pane's connector draft and is written once, on release.
 */
function useLabelDrag(item: ConnectorItem, geo: ConnectorGeometry, labelAt: number) {
  const board = useBoard();
  const pane = usePaneStore();
  const drag = useRef<{ pointerId: number; path: ConnectorPath; grab: Point; moved: boolean } | null>(null);

  const toWorld = (e: ReactPointerEvent): Point => {
    const host = (e.currentTarget as HTMLElement).closest('.viewport');
    const r = host?.getBoundingClientRect();
    const local = r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: e.clientX, y: e.clientY };
    return screenToWorld(pane.getState().camera, local);
  };

  const onUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    const s = pane.getState();
    const at = s.connectorDraft[item.id]?.labelAt;
    if (d.moved && at !== undefined && e.type === 'pointerup') board.updateItem(item.id, { props: { labelAt: at } });
    s.setConnectorDraft(item.id, null);
  };

  return {
    onPointerDown(e: ReactPointerEvent) {
      // Shift / Ctrl clicks change the selection as on any item
      if (e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const world = toWorld(e);
      const c = pointAlong(geo.path, labelAt);
      drag.current = { pointerId: e.pointerId, path: geo.path, grab: { x: world.x - c.x, y: world.y - c.y }, moved: false };
    },
    onPointerMove(e: ReactPointerEvent) {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const world = toWorld(e);
      let at = fractionNear(d.path, { x: world.x - d.grab.x, y: world.y - d.grab.y });
      if (distance(pointAlong(d.path, at), pointAlong(d.path, 0.5)) * pane.getState().camera.zoom < MIDDLE_SNAP_PX) at = 0.5;
      d.moved = true;
      pane.getState().setConnectorDraft(item.id, { labelAt: at });
    },
    onPointerUp: onUp,
    onPointerCancel: onUp,
  };
}

/**
 * A connector's label: a short line of text on its path, in a small box of the canvas colour so
 * the line breaks round it (inside a block arrow's band it sits on the fill instead). Placed in
 * the connector's item box, which `origin` is the top left of. While the connector is the one
 * thing selected, dragging the label slides it along the path; double-click (or Enter) types it.
 */
export function ConnectorLabel({
  item,
  props,
  geo,
  origin,
  selected,
}: {
  item: ConnectorItem;
  props: ConnectorProps;
  geo: ConnectorGeometry;
  origin: Point;
  selected: boolean;
}) {
  const board = useBoard();
  const readOnly = useReadOnly();
  const editable = !readOnly && !board.isLocked(item);
  const editing = usePane((s) => s.editingId === item.id) && editable;
  const alone = usePane((s) => s.selection.size === 1);
  const zoom = usePane((s) => s.settledZoom);
  const drag = useLabelDrag(item, geo, props.labelAt);
  if (!props.label && !editing) return null;
  const c = labelPoint(props, geo);
  const style: CSSProperties = { left: c.x - origin.x, top: c.y - origin.y, fontSize: props.labelSize, color: labelInk(props) };
  // in a block arrow the box takes the band's fill, so text wider than the band still reads
  if (props.kind === 'block') style.background = props.fill;
  const className = `connector-label${props.kind === 'block' ? ' is-in-band' : ''}`;
  if (editing) return <LabelEditor item={item} className={className} style={style} />;
  const movable = selected && alone && editable;
  return (
    <div className={`${className}${movable ? ' is-movable' : ''}`} style={selected ? { ...style, outlineWidth: 1.5 / zoom } : style} {...(movable ? drag : null)}>
      {props.label}
    </div>
  );
}

/** The label being typed: Enter, Esc or a click elsewhere commits it; left empty, the label goes. */
function LabelEditor({ item, className, style }: { item: ConnectorItem; className: string; style: CSSProperties }) {
  const board = useBoard();
  const pane = usePaneStore();
  const [text, setText] = useState(item.props.label);
  return (
    <input
      className={`${className} connector-label-editor`}
      // grows with the text (the board font is monospaced), with room for the caret
      style={{ ...style, width: `${Math.max(3, labelTextWidth(text, 1)) + 0.6}em` }}
      value={text}
      maxLength={200}
      placeholder="Label"
      aria-label="Arrow label"
      autoFocus
      spellCheck={false}
      onChange={(e) => setText(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Escape' || e.key === 'Enter') e.currentTarget.blur();
      }}
      onBlur={(e) => commitConnectorLabel({ board, pane }, item, e.currentTarget.value)}
      onFocus={(e) => e.currentTarget.select()}
    />
  );
}
